
import React, { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Spinner } from "@/components/ui/spinner";
import { SPECIAL_ADMIN_EMAILS } from "@/contexts/auth/constants";

interface ProtectedRouteProps {
  children: React.ReactNode;
  isAdmin?: boolean;
  isSalesAssistant?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  isAdmin = false,
  isSalesAssistant = false
}) => {
  const { user, loading, isAdmin: userIsAdmin, isSalesAssistant: userIsSalesAssistant } = useAuth();
  const location = useLocation();
  const [loadingTimeout, setLoadingTimeout] = useState(false);

  // Set a timeout to prevent infinite loading
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (loading) {
        console.log("Authentication check timed out, forcing navigation to login page");
        setLoadingTimeout(true);
      }
    }, 15000); // 15 seconds timeout

    return () => clearTimeout(timeoutId);
  }, [loading]);

  // Force redirect to login if loading times out
  if (loadingTimeout) {
    console.log("Loading timed out, redirecting to login page");
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // If we're still loading the auth state, show a loading indicator
  if (loading) {
    return (
      <div className="flex h-[calc(100vh-100px)] items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <Spinner size="md" />
          <p className="text-sm text-muted-foreground">
            Loading authentication...
          </p>
          <p className="text-xs text-muted-foreground mt-4">
            If this takes too long, <a href="/auth" className="text-blue-500 hover:underline">click here to login again</a>
          </p>
        </div>
      </div>
    );
  }

  // If the user is not authenticated, redirect to the login page
  if (!user) {
    console.log("User not authenticated, redirecting to login");
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Admins get full access to everything
  if (userIsAdmin || (user.email && SPECIAL_ADMIN_EMAILS.includes(user.email.toLowerCase()))) {
    console.log("Admin access granted, allowing full access");
    return <>{children}</>;
  }

  // If the route requires sales assistant access but the user is not a sales assistant
  if (isSalesAssistant && !userIsSalesAssistant) {
    console.log("Sales assistant access denied, redirecting to home");
    return <Navigate to="/" replace />;
  }

  // For regular protected routes, authenticated users can access
  return <>{children}</>;
};

export default ProtectedRoute;
